import { applyRenderScale } from '../render/render-scale';
import Phaser from 'phaser';
import { audioManager, inputManager, saveStore } from '../services';
import type { SettingsData } from '../sim/save';
import type { FlavorSelection } from '../ui/UIManager';
import { uiManager } from '../ui-instance';

interface MenuData {
  openSelect?: boolean;
}

export class MenuScene extends Phaser.Scene {
  private weather!: Phaser.GameObjects.Graphics;
  private elapsed = 0;
  private openSelect = false;
  private flavorSelection!: FlavorSelection;

  constructor() {
    super('MenuScene');
  }

  init(data: MenuData): void {
    this.openSelect = data.openSelect ?? false;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#8ad9f7');
    applyRenderScale(this.cameras.main);
    this.weather = this.add.graphics();
    audioManager.stopMusic();
    this.flavorSelection = {
      players: 1,
      flavors: [saveStore.data.selectedFlavor, saveStore.data.selectedSecondFlavor],
    };
    if (this.openSelect) this.showSelect();
    else this.showTitle();
  }

  update(_time: number, delta: number): void {
    this.elapsed += delta;
    // Soft daylight snowfall behind the DOM title, matching the arena art.
    this.weather.clear();
    this.weather.fillStyle(0xffffff, 0.16);
    this.weather.fillEllipse(640, 150, 1_180, 300);
    for (let index = 0; index < 70; index += 1) {
      const drift = Math.sin(this.elapsed / 1_500 + index) * 22;
      const x = (index * 193 + drift + 1_280) % 1_280;
      const y = (index * 97 + this.elapsed * (index % 3 + 2) * 0.016) % 780 - 20;
      const alpha = 0.4 + (Math.sin(this.elapsed / 700 + index) + 1) * 0.22;
      this.weather.fillStyle(0xffffff, alpha);
      this.weather.fillCircle(x, y, index % 6 === 0 ? 3.4 : 2);
    }
  }

  private showTitle(): void {
    uiManager.showTitle({
      onPlay: () => {
        void audioManager.unlock();
        this.showFlavorSelect();
      },
      onSettings: () => this.showSettings(() => this.showTitle()),
    });
  }

  private showFlavorSelect(): void {
    uiManager.showFlavorSelect(saveStore.data, {
      onContinue: (selection) => {
        this.flavorSelection = selection;
        saveStore.data.selectedFlavor = selection.flavors[0];
        saveStore.data.selectedSecondFlavor = selection.flavors[1];
        saveStore.persist();
        this.showSelect();
      },
      onBack: () => this.showTitle(),
      onSettings: () => this.showSettings(() => this.showFlavorSelect()),
    });
  }

  private showSelect(): void {
    const debug = new URLSearchParams(window.location.search).get('debug') === '1';
    uiManager.showLevelSelect(saveStore.data, debug, this.flavorSelection, {
      onStart: (selection) => {
        void audioManager.unlock();
        this.scene.start('GameScene', selection);
      },
      onBack: () => this.showFlavorSelect(),
      onSettings: () => this.showSettings(() => this.showSelect()),
    });
  }

  private showSettings(onBack: () => void): void {
    uiManager.showSettings(saveStore.data.settings, inputManager, {
      onChange: (settings: SettingsData) => {
        saveStore.data.settings = { ...settings, controls: { ...settings.controls } };
        inputManager.setBindings(saveStore.data.settings.controls);
        audioManager.setVolumes(settings.musicVolume, settings.sfxVolume);
        document.body.classList.toggle('reduced-motion', settings.reducedMotion);
        document.body.classList.toggle('high-contrast', settings.highContrast);
        saveStore.persist();
      },
      onBack,
    });
  }
}
